import { useEffect, useState } from "react";
import { Gift, Copy, Star } from "lucide-react";
import { toast } from "sonner";
import clientApi from "@/lib/clientApi";
import api from "@/lib/api";

const WA_TEXT = (link) =>
  encodeURIComponent(`We just planned our move to Mexico with Hola Movers and loved it! If you're thinking about it too, use my link: ${link}`);

export const ReferAndEarn = () => {
  const [data, setData] = useState(null);
  const [reviewLink, setReviewLink] = useState("");

  useEffect(() => {
    clientApi.get("/client/referral").then((r) => setData(r.data)).catch(() => {});
    api.get("/brand").then((r) => setReviewLink(r.data.google_review_link || "")).catch(() => {});
  }, []);

  if (!data) return null;
  const shareLink = `${window.location.origin}/r/${data.code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Referral link copied — share it anywhere!");
    } catch { toast.error("Could not copy. Long-press the link to copy it."); }
  };

  return (
    <section data-testid="portal-referral" className="rounded-3xl border border-terracotta/30 bg-terracotta/5 p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terracotta/15 text-terracotta">
          <Gift size={22} strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-xl text-charcoal-800">Refer a friend, earn ${data.reward_amount}</h2>
          <p className="mt-1 text-sm text-charcoal-600 leading-relaxed">
            Know someone dreaming about Mexico? Share your personal link below. Once they relocate with us,
            we'll send you <span className="font-medium text-terracotta">${data.reward_amount}</span> as a thank-you.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <code data-testid="portal-referral-link" className="flex-1 truncate rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-sm text-charcoal-700">
              {shareLink}
            </code>
            <div className="flex gap-2">
              <button data-testid="portal-referral-copy" onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover transition-colors">
                <Copy size={15} /> Copy
              </button>
              <a data-testid="portal-referral-whatsapp" target="_blank" rel="noopener noreferrer"
                href={`https://wa.me/?text=${WA_TEXT(shareLink)}`}
                className="inline-flex items-center rounded-full border border-terracotta px-4 py-2.5 text-sm font-medium text-terracotta hover:bg-terracotta hover:text-white transition-colors">
                WhatsApp
              </a>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-charcoal-500" data-testid="portal-referral-stats">
            <span>{data.stats.clicks} link visit{data.stats.clicks === 1 ? "" : "s"}</span>
            <span>{data.stats.referred} referred</span>
            <span>{data.stats.relocated} relocated</span>
            <span className="font-medium text-terracotta">${data.stats.earned} earned</span>
          </div>

          {reviewLink && (
            <a data-testid="portal-review-link" href={reviewLink} target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-terracotta transition-colors">
              <Star size={15} className="text-terracotta" /> Loved working with us? Leave a Google review
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
